<template>
  <div class="tw-flex-column tw-justify-between tw-gap-5">
    <div class="tw-flex tw-flex-row tw-gap-5 tw-ml-5 ">
      <generic-filters @resetFilters="resetFilters">
        <form-field
          label="Zoek adres"
          type="text"
          v-model="queryInput"
          class="tw-w-[250px] tw-mr-5"
        />
        <form-field
          label="Case status"
          nullOption="Alle statussen"
          :options="statusOptions"
          type="select"
          v-model="filter.status"
          class="tw-w-[250px] tw-mr-5"
        />
        <form-field
          label="Woco"
          nullOption="Alle woco's"
          :options="wocos"
          type="multi"
          mode="tags"
          v-model="filter.wocos"
          class="tw-w-[250px] tw-mr-5"
        />
        <form-field
          label="Categorie"
          nullOption="Alle categorieën"
          :options="categories"
          type="select"
          mode="tags"
          v-model="filter.category"
          class="tw-w-[250px] tw-mr-5"
        />
        <form-field
          label="Partner"
          nullOption="Alle partners"
          :options="partners"
          type="select"
          mode="tags"
          v-model="filter.partner"
          class="tw-w-[250px] tw-mr-5"
        />
        <form-field
          label="Ticket status"
          nullOption="Alle statuses"
          :options="ticketStatusOptions"
          type="select"
          mode="tags"
          v-model="filter.ticketStatus"
          class="tw-w-[250px] tw-mr-5"
        />
        <form-field
          label="Alarm status"
          :options="alarmStatusOptions"
          type="select"
          v-model="filter.alarmStatus"
          class="tw-w-[250px] tw-mr-5"
        />
        <form-field
          label="Geopend sinds"
          :options="openedSinceOptions"
          type="select"
          v-model="filter.openedSince"
          class="tw-w-[250px] tw-mr-5"
        />
      </generic-filters>
      <div class="tw-w-full tw-flex tw-gap-5 tw-mr-2">

        <div class="tw-my-2 tw-flex-auto tw-flex-col">

          <div
            v-if="canCreateCase && selectedRows.length > 0"
            class="tw-flex tw-gap-2 tw-items-center tw-bg-gray-100 tw-border tw-border-gray-200 tw-rounded tw-p-2 tw-mb-2"
          >
            <div class="tw-font-bold tw-text-gray-600 tw-mr-2">
              {{ selectedRows.length }} geselecteerd
            </div>
            <button
              v-for="bulk in bulkActions"
              :key="bulk.action"
              :class="bulk.classes"
              class="tw-rounded tw-p-1 tw-px-2 tw-text-white disabled:tw-opacity-50"
              :disabled="bulkLoading"
              @click="showBulkConfirmation(bulk.action)"
            >
              {{ bulk.label }}
            </button>
            <button
              class="tw-bg-green-600 hover:tw-bg-green-500 tw-rounded tw-p-1 tw-px-2 tw-text-white disabled:tw-opacity-50"
              :disabled="bulkLoading"
              @click="openTicketsForSelection"
            >
              Maak tickets
            </button>
            <button
              class="tw-text-gray-500 hover:tw-text-gray-700 tw-p-1 tw-ml-auto"
              @click="selectedIds = []"
            >
              Deselecteer
            </button>
          </div>

          <generic-table
            :columns="columns"
            :data="cases"
            :has-next-page="hasNextPage"
            :is-loading="isLoading"
            :items-amount="numberOfCases"
            :rowClickHandler="redirectToCaseDetail"
            :selectable="canCreateCase"
            :selected-ids.sync="selectedIds"
            @fetchNextPage="fetchNextPage()"
          >
            <template v-slot:buttons>
              <div>
                <button
                  class="tw-bg-green-600 hover:tw-bg-green-500 tw-rounded tw-flex tw-gap-2 tw-items-center tw-p-2 tw-text-white"
                  @click.stop="showModal = true"
                >
                  <PlusIcon class="tw-w-5 tw-h-5" />
                  Nieuwe case
                </button>
              </div>
              <div v-if="canCreateCase">
                <button
                  class="tw-bg-green-600 hover:tw-bg-green-500 tw-rounded tw-flex tw-gap-2 tw-items-center tw-p-2 tw-text-white"
                  @click.stop="showBatchModal = true"
                >
                  <PlusIcon class="tw-w-5 tw-h-5" />
                  Nieuwe cases (batch)
                </button>
              </div>
              <div>
                <button
                  class="tw-bg-blue-500 hover:tw-bg-blue-400 tw-rounded tw-flex tw-gap-2 tw-items-center tw-p-2 tw-text-white"
                  @click="downloadCasesExcel"
                >
                  <DocumentIcon class="tw-w-5 tw-h-5" />
                  Exporteer
                </button>
              </div>
            </template>

            <template v-slot:cell-status="{ row }">
              <CaseStatus :status="row.status" />
            </template>

            <template v-slot:cell-address="{ row }">
              <div>{{ row.location?.address.street }} {{ row.location?.address.house_number }}</div>
              <div class="tw-text-xs">{{ row.location?.address.postal_code }} {{ row.location?.address.city }}</div>
            </template>
            <template v-slot:cell-started_at="{ row }">
              <div v-if="row.opened_at && !row.finished_at && !row.cancelled_at">
                {{ daysSince(row.opened_at) }} dagen
                <span v-if="daysSince(row.opened_at) > 14" class="tw-text-orange-500">&#33</span>
                <span v-if="daysSince(row.opened_at) > 30" class="tw-text-red-500">&#33</span>
              </div>
            </template>

            <template v-slot:cell-alarm="{ row }">
              <AlarmStatus v-if="row.alarm" :alarm="row.alarm" />
            </template>

            <template v-slot:cell-current_ticket="{ row }">
              <div v-if="row.current_ticket?.status" class="tw-flex tw-gap-2">
                <div :class="ticketStatusBgColorClass[row.current_ticket.status]" class="tw-text-xs tw-p-1 tw-rounded-sm tw-tracking-wider">
                  {{ ticketStatuses[row.current_ticket.status] }}
                </div>
              </div>
            </template>

            <template v-slot:cell-partner="{ row }">
              <div v-if="row.current_ticket?.partner" class="tw-flex tw-gap-2">
                <div>{{ row.current_ticket.partner }}</div>
              </div>
            </template>

            <template v-slot:cell-last_contact_at="{ row }">
              <div v-if="row.last_contact_at">
                {{ daysSince(row.last_contact_at) }} dagen ({{ row.contact_logs_count }})
              </div>
            </template>

          </generic-table>
        </div>
        <div
          class="tw-mr-5 tw-flex-auto tw-flex-col tw-w-[400px] tw-max-w-[400px]"
          v-if="$route.params.id"
        >
          <case-detail :id="$route.params.id" />
        </div>
      </div>
    </div>

    <CreateCaseModal
      v-if="showModal"
      @close="showModal = false"
      @created="caseCreated"
    />

    <BatchCreateCasesModal
      v-if="showBatchModal"
      @close="showBatchModal = false"
      @createTickets="openTicketsForCases"
    />

    <BatchCreateTicketsModal
      v-if="ticketsModal.open"
      :case-ids="ticketsModal.caseIds"
      :initial-title="ticketsModal.title"
      @close="ticketsModal.open = false"
    />

    <generic-modal
      v-if="bulkConfirm.action"
      @close="bulkConfirm.action = null"
      @submit="confirmBulkAction"
      :title="bulkConfirmTitle"
      :disabled="bulkEligibleRows.length === 0"
    >
      <div>
        {{ bulkEligibleRows.length }} van de {{ selectedRows.length }} geselecteerde cases komen in
        aanmerking voor deze actie.
      </div>
      <div v-if="bulkEligibleRows.length < selectedRows.length" class="tw-text-orange-500 tw-text-sm">
        De overige cases worden overgeslagen (verkeerde status of nog een actief ticket).
      </div>
      <div v-if="bulkConfirm.action === 'mute'" class="tw-mt-2">
        <FormField
          label="Periode"
          type="select"
          v-model="bulkConfirm.period"
          :options="MUTE_PERIODS"
        />
      </div>
    </generic-modal>
  </div>
</template>

<script setup>
import { reactive, computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router/composables';
import { watchDebounced } from '@vueuse/core'
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/vue-query';
import { actions } from '@/store';
import { notifications, mapWithConcurrency } from '@/util';
import fetchData, { fetchResponse } from '@/util/fetch';
import CaseDetail from '@/components/TicketCases/CaseDetail.vue';
import GenericFilters from '@/components/shared/GenericFilters.vue';
import GenericTable from '@/components/shared/GenericTable.vue';
import GenericModal from '@/components/shared/GenericModal.vue';
import FormField from '@/components/shared/FormField.vue';
import { parseISO, differenceInDays } from 'date-fns';
import {
  caseActionAvailable,
  caseActionPayload,
  hasBlockingTicket,
  INACTIVE_TICKET_STATUSES,
  MUTE_PERIODS,
} from '@/logic/common/CaseActions';
import {
  ticketStatuses,
  ticketStatusBgColorClass,
  CASE_CATEGORIES,
  MAX_BATCH_SIZE,
  MAX_CONCURRENT_BULK_ACTIONS,
} from '@/constants';
import {
  DocumentIcon,
  PlusIcon,
} from '@vue-hero-icons/outline';
import { hasPermission } from '@/logic/common/Permissions';
import AlarmStatus from '@/components/TicketCases/AlarmStatus.vue';
import CaseStatus from '@/components/TicketCases/CaseStatus.vue';
import CreateCaseModal from '@/components/TicketCases/CreateCaseModal.vue';
import BatchCreateCasesModal from '@/components/TicketCases/BatchCreateCasesModal.vue';
import BatchCreateTicketsModal from '@/components/TicketCases/BatchCreateTicketsModal.vue';

const router = useRouter();
const queryClient = useQueryClient();

const ticketStatusOptions = Object.entries(ticketStatuses).map(([key, value]) => ({
  value: key,
  label: value,
})).concat({ value: 'no tickets', label: 'Geen tickets' });

const alarmStatusOptions = [
  { value: 'all', label: 'Alle statussen' },
  { value: 'open', label: 'Open alarmen' },
  { value: 'closed', label: 'Gesloten alarmen' },
  { value: 'no_status', label: 'Geen alarm' }
];

const openedSinceOptions = [
  { value: 'newest', label: 'Van nieuwste' },
  { value: 'oldest', label: 'Van oudste' }
];

const showModal = ref(false);
const showBatchModal = ref(false);

const canCreateCase = computed(() => hasPermission(['Tickets:create_case']));

function daysSince(date) {
  return differenceInDays(new Date(), parseISO(date));
}

function caseCreated(caseDetail) {
  showModal.value = false;
  router.push({ name: 'case-detail', params: { id: caseDetail.id } })
}

const filter = reactive({
  status: 'open',
  wocos: [],
  category: '',
  partner: '',
  ticketStatus: '',
  alarmStatus: 'all',
  openedSince: 'newest',
  query: '',
});

const resetFilters = () => {
  filter.status = 'open';
  filter.wocos = [];
  filter.category = '';
  filter.partner = '';
  filter.ticketStatus = '';
  filter.alarmStatus = 'all';
  filter.openedSince = 'newest';
  filter.query = '';
  queryInput.value = '';
};

const queryInput = ref('')

watchDebounced(
  queryInput,
  () => {
    filter.query = queryInput.value
  },
  {
    debounce: 300,
  }
)

const redirectToCaseDetail = (row) => {
  if (row.id === router?.currentRoute?.params?.id) return
  router.push({
    name: 'case-detail',
    params: { id: row.id, type: 'id' },
  })
}

const statusOptions = ['created', 'open', 'finished', 'cancelled', 'muted'];
const categories = CASE_CATEGORIES

const getWocos = () => fetchData('/config/api/v1/wocos/?view=full');
const wocosQuery = useQuery({
  queryKey: ['wocos'],
  queryFn: getWocos,
});
const wocos = computed(() =>
  wocosQuery.data?.value?.data.map((w) => ({ value: w.shortname, label: w.name }))
);

const getPartners = () => fetchData('/tickets/api/v1/partners');
const partnersQuery = useQuery({
  queryKey: ['partners'],
  queryFn: getPartners,
});
const partners = computed(() => partnersQuery.data?.value?.data.map((p) => p.legacy_name) || [])

const perPage = 30;
const token = actions.auth.readToken();

const getList = async ({ pageParam = 1 }) => {
  const { status, wocos, category, partner, ticketStatus, query, alarmStatus, openedSince } = filter;

  const urlParams = new URLSearchParams({
    offset: (pageParam - 1) * perPage,
    limit: perPage,
  });
  if (wocos) {
    wocos.forEach((woco) => {
      urlParams.append('woco_names', woco);
    });
  }
  if (status) {
    urlParams.append('status', status);
  }
  if (category) {
    urlParams.append('category', category);
  }
  if (partner) {
    urlParams.append('current_ticket_partner', partner);
  }
  if (ticketStatus) {
    urlParams.append('current_ticket_status', ticketStatus);
  }
  if (alarmStatus && alarmStatus !== 'all') {
    urlParams.append('alarm_status', alarmStatus);
  }
  if (openedSince && openedSince !== 'newest') {
    urlParams.append('opened_since', openedSince);
  }
  if (query) {
    urlParams.append('query', query);
  }

  try {
    const result = await fetch(`/tickets/api/v1/cases?${urlParams}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!result.ok) {
      notifications.addNotification({
        message: `Failed to get list of cases: ${result.statusText}`,
        type: 'danger',
      });
      return;
    }
    return await result.json();
  } catch (error) {
    notifications.addNotification({
      message: `Failed to get list of cases, ${e.message || e.detail}`,
      type: 'danger',
    });
    throw new Error('Failed to get list of cases with error: ' + error.message);
  }
};

const {
  data: casesQuery,
  isLoading,
  fetchNextPage,
  hasNextPage,
} = useInfiniteQuery({
  queryKey: computed(() => ['cases', filter]),
  keepPreviousData: true,
  queryFn: getList,
  getNextPageParam: (_, allPages) =>
    allPages[allPages.length - 1].data?.length === perPage ? allPages.length + 1 : undefined,
});

const numberOfCases = computed(() => casesQuery?.value?.pages[0]?.total);
const cases = computed(() => casesQuery?.value?.pages.flatMap((p) => p.data));

const selectedIds = ref([]);

watch(filter, () => {
  selectedIds.value = [];
});

const selectedRows = computed(() => {
  const ids = new Set(selectedIds.value);
  return (cases.value || []).filter((row) => row && ids.has(row.id));
});

// prune ids of rows that disappeared on a refetch, so a row that later
// reappears does not come back pre-selected
watch(cases, (rows) => {
  if (!rows) return;
  const ids = new Set(rows.filter(Boolean).map((row) => row.id));
  if (selectedIds.value.some((id) => !ids.has(id))) {
    selectedIds.value = selectedIds.value.filter((id) => ids.has(id));
  }
});

const bulkActions = [
  { action: 'open', label: 'Open', classes: 'tw-bg-blue-500 hover:tw-bg-blue-400' },
  { action: 'mute', label: 'Mute', classes: 'tw-bg-gray-500 hover:tw-bg-gray-400' },
  { action: 'finish', label: 'Finish', classes: 'tw-bg-green-500 hover:tw-bg-green-400' },
  { action: 'cancel', label: 'Cancel', classes: 'tw-bg-red-500 hover:tw-bg-red-400' },
];

const bulkConfirm = reactive({
  action: null,
  period: '1 week',
});

const bulkConfirmTitle = computed(() => {
  if (bulkConfirm.action === 'mute') return 'Voor hoe lang wil je deze cases muten?';
  const verb = { open: 'openen', finish: 'afronden', cancel: 'annuleren' }[bulkConfirm.action];
  return `Weet je zeker dat je deze cases wilt ${verb}?`;
});

const bulkLoading = ref(false);

const ticketsModal = reactive({ open: false, caseIds: [], title: '' });

const openTicketsModal = (caseIds, title = '') => {
  ticketsModal.caseIds = caseIds;
  ticketsModal.title = title;
  ticketsModal.open = true;
};

const openTicketsForSelection = () => {
  if (selectedRows.value.length > MAX_BATCH_SIZE) {
    notifications.addNotification({
      message: `Maximaal ${MAX_BATCH_SIZE} cases per batch, verklein de selectie`,
      type: 'danger',
    });
    return;
  }
  openTicketsModal(selectedRows.value.map((row) => row.id));
};

const openTicketsForCases = ({ caseIds, title }) => {
  showBatchModal.value = false;
  openTicketsModal(caseIds, title || '');
};

// pre-filter only; the authoritative all-tickets check runs per case in confirmBulkAction
const isEligible = (row, action) => {
  if (!caseActionAvailable(action, row.status)) return false;
  if (action === 'finish' || action === 'cancel') {
    return !row.current_ticket || INACTIVE_TICKET_STATUSES.includes(row.current_ticket.status);
  }
  return true;
};

const bulkEligibleRows = computed(() =>
  bulkConfirm.action ? selectedRows.value.filter((row) => isEligible(row, bulkConfirm.action)) : []
);

const showBulkConfirmation = (action) => {
  bulkConfirm.action = action;
  bulkConfirm.period = '1 week';
};

const confirmBulkAction = async () => {
  const action = bulkConfirm.action;
  const period = bulkConfirm.period;
  const rows = bulkEligibleRows.value;
  const preSkipped = selectedRows.value.length - rows.length;
  bulkConfirm.action = null;
  bulkLoading.value = true;

  try {
    const results = await mapWithConcurrency(rows, MAX_CONCURRENT_BULK_ACTIONS, async (row) => {
      try {
        if (action === 'finish' || action === 'cancel') {
          // the table row carries only the latest ticket; any open ticket blocks closing
          const tickets = await fetchData(`/tickets/api/v1/tickets?view=list&case_id=${row.id}`);
          if (hasBlockingTicket(tickets.data || [])) return 'skipped';
        }

        const response = await fetchResponse(`/tickets/api/v1/cases/${row.id}`, {
          method: 'PUT',
          body: JSON.stringify(caseActionPayload(action, row.status, period)),
        });
        return response.ok ? 'ok' : 'failed';
      } catch (e) {
        console.error(`Bulk ${action} failed for case ${row.id}`, e);
        return 'failed';
      }
    });

    const succeeded = results.filter((r) => r === 'ok').length;
    const skipped = preSkipped + results.filter((r) => r === 'skipped').length;
    const failed = results.filter((r) => r === 'failed').length;

    const parts = [`${succeeded} cases bijgewerkt`];
    if (skipped > 0) parts.push(`${skipped} overgeslagen`);
    if (failed > 0) parts.push(`${failed} mislukt`);
    notifications.addNotification({
      message: parts.join(', '),
      type: failed > 0 ? 'danger' : 'success',
    });

    selectedIds.value = [];
    queryClient.invalidateQueries({ queryKey: ['cases'] });
  } finally {
    bulkLoading.value = false;
  }
};

async function downloadCasesExcel() {
  const { status, wocos, category, partner, ticketStatus, query, alarmStatus, openedSince } = filter;
  const urlParams = new URLSearchParams({});
  if (wocos) {
    wocos.forEach((woco) => {
      urlParams.append('woco_names', woco);
    });
  }
  if (status) {
    urlParams.append('status', status);
  }
  if (category) {
    urlParams.append('category', category);
  }
  if (partner) {
    urlParams.append('current_ticket_partner', partner);
  }
  if (ticketStatus) {
    urlParams.append('current_ticket_status', ticketStatus);
  }
  if (alarmStatus && alarmStatus !== 'all') {
    urlParams.append('alarm_status', alarmStatus);
  }
  if (openedSince && openedSince !== 'newest') {
    urlParams.append('opened_since', openedSince);
  }
  if (query) {
    urlParams.append('query', query);
  }

  const result = await fetch(`/tickets/api/v1/cases?view=excel&${urlParams}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (result.ok) {
    await downloadAsFile(result);
  } else {
    const error = await result.json();
    console.error(error);
    notifications.addNotification({
      message: `Something went wrong: ${error.detail}`,
      type: 'danger',
    });
  }
}

async function downloadAsFile(result) {
  const match = /filename="([^"]+)"/.exec(result.headers.get('content-disposition'));
  const filename = match[1];
  console.log(filename);

  const element = document.createElement('a');
  const blob = new Blob([await result.blob()], {
    type: 'application/vnd.ms-excel',
  });
  element.href = window.URL.createObjectURL(blob);
  element.setAttribute('download', filename);
  element.click();
}

const columns = [
  { label: 'Adres', field: 'address' },
  { label: 'Woco', field: 'woco_name' },
  { label: 'Categorie', field: 'category' },
  { label: 'Open sinds', field: 'started_at' },
  { label: 'Case status', field: 'status' },
  { label: 'Alarm status', field: 'alarm' },
  { label: 'Laatste ticket', field: 'current_ticket' },
  { label: 'Partner', field: 'partner' },
  { label: 'Laatste contactpoging', field: 'last_contact_at' },
];
</script>
